<template>
  <div class="draft-management-page">
    <div class="settings-card">
      <h2 class="page-title">
        <Icon icon="heroicons:document-duplicate" width="24" height="24" />
        图文任务
      </h2>

      <!-- 搜索区域 -->
      <div class="search-bar">
        <div class="search-left">
          <el-input
            v-model="searchForm.task_name"
            placeholder="请输入任务名称"
            clearable
            size="large"
            class="search-input"
            @keyup.enter="handleSearch"
          >
            <template #prefix>
              <Icon icon="heroicons:magnifying-glass" width="18" height="18" />
            </template>
          </el-input>

          <el-button
            type="primary"
            size="large"
            @click="handleSearch"
            class="search-btn"
          >
            <Icon icon="heroicons:magnifying-glass" width="18" height="18" />
            <span class="btn-text">搜索</span>
          </el-button>
        </div>

        <el-button type="success" size="large" @click="openAddDialog" class="add-btn">
          <Icon icon="heroicons:plus" width="18" height="18" />
          <span class="btn-text">新增任务</span>
        </el-button>
      </div>

      <!-- 表格包装器 -->
      <div class="table-wrapper">
        <el-table
          :data="taskList"
          stripe
          v-loading="loading"
          class="draft-table"
          :row-style="{ background: 'transparent' }"
          :cell-style="{ background: 'transparent' }"
          :header-cell-style="{ background: 'rgba(157, 78, 221, 0.15)', color: '#fff' }"
        >
          <el-table-column prop="task_name" label="任务名称" width="180" />

          <el-table-column label="账号" width="150">
            <template #default="{ row }">
              <span>{{ getGzhName(row.gzh_id) }}</span>
            </template>
          </el-table-column>

          <el-table-column label="账号类型" width="120">
            <template #default="{ row }">
              <el-tag :type="getPlatformTagType(getGzhFieldType(row.gzh_id))" size="small">
                {{ getPlatformText(getGzhFieldType(row.gzh_id)) }}
              </el-tag>
            </template>
          </el-table-column>

          <el-table-column label="图片分类" width="150">
            <template #default="{ row }">
              <span>{{ getCategoryName(row.tp_id) }}</span>
            </template>
          </el-table-column>

          <el-table-column label="图片总数" width="140" align="center">
            <template #default="{ row }">
              <el-tag type="info" size="small">{{ row.category_total_articles }}</el-tag>
            </template>
          </el-table-column>

          <el-table-column label="已使用图片" width="120" align="center">
            <template #default="{ row }">
              <el-tag type="warning" size="small">{{ row.category_used_articles }}</el-tag>
            </template>
          </el-table-column>

          <el-table-column label="今日已发送" width="120" align="center">
            <template #default="{ row }">
              <el-tag type="success" size="small">{{ row.today_tasks }}</el-tag>
            </template>
          </el-table-column>

          <el-table-column label="每日发布数" width="120">
            <template #default="{ row }">
              <el-tag type="info">{{ row.daily_maximum_release }}</el-tag>
            </template>
          </el-table-column>

          <el-table-column label="已发布总数" width="120">
            <template #default="{ row }">
              <el-tag type="warning">{{ row.total_tasks }}</el-tag>
            </template>
          </el-table-column>

          <el-table-column label="总发布数" width="120">
            <template #default="{ row }">
              <el-tag type="success">{{ row.total_publications }}</el-tag>
            </template>
          </el-table-column>

          <el-table-column label="错误信息" width="180">
            <template #default="{ row }">
              <el-tooltip v-if="row.error_message" :content="row.error_message" placement="top">
                <el-tag type="danger" size="small">错误</el-tag>
              </el-tooltip>
              <el-tag v-else type="info" size="small">无错误</el-tag>
            </template>
          </el-table-column>

          <el-table-column label="最后发布时间" width="180">
            <template #default="{ row }">
              <span>{{ formatDateTime(row.last_execution_time) || '未发布' }}</span>
            </template>
          </el-table-column>

          <el-table-column prop="create_time" label="创建时间" width="180" />
          <el-table-column prop="update_time" label="更新时间" width="180" />

          <el-table-column label="目前状态" width="120" fixed="right">
            <template #default="{ row }">
              <el-tag :type="getStatusTagType(row.status)" size="small">
                {{ getStatusText(row.status) }}
              </el-tag>
            </template>
          </el-table-column>

          <el-table-column label="启动状态" width="120" fixed="right">
            <template #default="{ row }">
              <el-button
                :type="row.qd_status === 1 ? 'warning' : 'success'"
                size="small"
                @click="toggleTaskStatus(row)"
                :loading="row.statusLoading"
              >
                {{ row.qd_status === 1 ? '暂停任务' : '启动任务' }}
              </el-button>
            </template>
          </el-table-column>

          <el-table-column label="操作" width="200" fixed="right">
            <template #default="scope">
              <button class="action-btn edit-btn" @click="openEditDialog(scope.row)">
                <Icon icon="heroicons:pencil-square" width="14" height="14" />
                <span class="btn-text">编辑</span>
              </button>
              <button class="action-btn delete-btn" @click="handleDelete(scope.row.id)">
                <Icon icon="heroicons:trash" width="14" height="14" />
                <span class="btn-text">删除</span>
              </button>
            </template>
          </el-table-column>
        </el-table>
      </div>

      <!-- 分页组件 -->
      <div class="pagination-container">
        <el-pagination
          v-model:current-page="pagination.page"
          v-model:page-size="pagination.pageSize"
          :total="pagination.total"
          :page-sizes="[10, 20, 30, 50]"
          :layout="paginationLayout"
          @size-change="fetchTaskList"
          @current-change="fetchTaskList"
          background
        />
      </div>
    </div>

    <!-- 新增/编辑对话框 -->
    <el-dialog
      v-model="dialogVisible"
      :title="dialogTitle"
      :width="dialogWidth"
      :close-on-click-modal="false"
      class="custom-dialog"
      :top="dialogTop"
    >
      <el-form ref="taskForm" :model="taskForm" :label-width="formLabelWidth" :rules="formRules">
        <el-row :gutter="20">
          <el-col :span="12">
            <!-- 任务名称 -->
            <el-form-item label="任务名称" prop="task_name">
              <el-input v-model="taskForm.task_name" placeholder="请输入任务名称" size="large" />
            </el-form-item>

            <!-- 账号选择 -->
            <el-form-item label="账号" prop="gzh_id">
              <div class="account-select-container">
                <el-select
                  v-model="taskForm.gzh_id"
                  placeholder="请选择账号"
                  size="large"
                  multiple
                  filterable
                  class="account-select"
                >
                  <el-option
                    v-for="gzh in gzhList"
                    :key="gzh.id"
                    :label="`${gzh.ghz_name}(${getPlatformText(gzh.field_type)})`"
                    :value="gzh.id"
                  >
                    <span>{{ gzh.ghz_name }}</span>
                    <span class="platform-tag" :style="{ backgroundColor: getPlatformBgColor(gzh.field_type) }">
                      {{ getPlatformText(gzh.field_type) }}
                    </span>
                  </el-option>
                </el-select>

                <div class="selection-actions">
                  <div class="quick-selection">
                    <span class="action-label">快速选择：</span>
                    <button class="selection-btn select-all-btn" @click="selectAllAccounts">
                      <Icon icon="heroicons:check-circle" width="14" height="14" />
                      <span>全选</span>
                    </button>
                    <button class="selection-btn clear-all-btn" @click="clearAllAccounts">
                      <Icon icon="heroicons:x-circle" width="14" height="14" />
                      <span>清空</span>
                    </button>
                  </div>

                  <div class="platform-selection">
                    <span class="action-label">按平台选择：</span>
                    <div class="platform-tags">
                      <el-tag
                        v-for="platform in platformTypes"
                        :key="platform.value"
                        :style="{
                          background: getPlatformBgColor(platform.value),
                          color: '#fff',
                          cursor: 'pointer',
                          margin: '2px',
                          border: 'none'
                        }"
                        size="small"
                        @click="selectPlatformAccounts(platform.value)"
                      >
                        {{ platform.label }}
                      </el-tag>
                    </div>
                  </div>
                </div>
              </div>
            </el-form-item>

            <!-- 作者名称 -->
            <el-form-item label="作者名称">
              <el-input
                v-model="taskForm.gzh_author"
                placeholder="请输入作者名称"
                size="large"
                maxlength="8"
                show-word-limit
              />
            </el-form-item>

            <!-- 标题分组 -->
            <el-form-item label="标题分组" prop="title_id">
              <el-select
                v-model="taskForm.title_id"
                placeholder="请选择标题分组"
                size="large"
                style="width: 100%"
                clearable
                filterable
              >
                <el-option
                  v-for="item in titleList"
                  :key="item.id"
                  :label="item.title_name"
                  :value="item.id"
                />
              </el-select>
            </el-form-item>

            <!-- 描述分组 -->
            <el-form-item label="描述分组" prop="desc_id">
              <el-select
                v-model="taskForm.desc_id"
                placeholder="请选择描述分组"
                size="large"
                style="width: 100%"
                clearable
                filterable
              >
                <el-option
                  v-for="item in descList"
                  :key="item.id"
                  :label="item.desc_name"
                  :value="item.id"
                />
              </el-select>
            </el-form-item>

            <!-- 话题分组 -->
            <el-form-item label="话题分组" prop="topic_id">
              <el-select
                v-model="taskForm.topic_id"
                placeholder="请选择话题分组"
                size="large"
                style="width: 100%"
                clearable
                filterable
              >
                <el-option
                  v-for="item in topicList"
                  :key="item.id"
                  :label="item.topic_name"
                  :value="item.id"
                />
              </el-select>
            </el-form-item>
          </el-col>

          <el-col :span="12">
            <!-- 标题前缀 -->
            <el-form-item label="标题前缀">
              <el-input v-model="taskForm.add_prefix_to_title" placeholder="多个前缀用逗号分隔" size="large" />
            </el-form-item>

            <!-- 标题后缀 -->
            <el-form-item label="标题后缀">
              <el-input v-model="taskForm.title_with_suffix" placeholder="多个后缀用逗号分隔" size="large" />
            </el-form-item>

            <!-- 图片数量 -->
            <el-form-item label="图片数量" prop="tp_num">
              <el-input-number v-model="taskForm.tp_num" :min="1" :max="9" size="large" style="width: 100%" />
            </el-form-item>

            <!-- 图库分组 -->
            <el-form-item label="图库分组" prop="tp_id">
              <el-select
                v-model="taskForm.tp_id"
                placeholder="请选择图库分组"
                size="large"
                style="width: 100%"
                filterable
              >
                <el-option
                  v-for="item in libraryList"
                  :key="item.id"
                  :label="item.tuku_name"
                  :value="item.id"
                />
              </el-select>
            </el-form-item>

            <!-- 挂载类型 -->
            <el-form-item label="挂载类型">
              <el-radio-group v-model="mountingType" size="large" @change="handleMountingTypeChange">
                <el-radio-button label="shop">商品挂载</el-radio-button>
                <el-radio-button label="poi">地址挂载</el-radio-button>
                <el-radio-button label="mini">程序挂载</el-radio-button>
                <el-radio-button label="none">无需挂载</el-radio-button>
              </el-radio-group>
            </el-form-item>

            <!-- 商品挂载 -->
            <el-form-item v-if="mountingType === 'shop'" label="商品挂载" prop="shop_mounting_id">
              <el-select
                v-model="taskForm.shop_mounting_id"
                placeholder="请选择商品挂载"
                size="large"
                style="width: 100%"
                filterable
                clearable
              >
                <el-option
                  v-for="item in shopMountingList"
                  :key="item.id"
                  :label="item.name"
                  :value="item.id"
                />
              </el-select>
            </el-form-item>

            <!-- POI挂载 -->
            <el-form-item v-if="mountingType === 'poi'" label="POI挂载" prop="poi_mounting_id">
              <el-select
                v-model="taskForm.poi_mounting_id"
                placeholder="请选择POI挂载"
                size="large"
                style="width: 100%"
                filterable
                clearable
              >
                <el-option
                  v-for="item in poiMountingList"
                  :key="item.id"
                  :label="item.name"
                  :value="item.id"
                />
              </el-select>
            </el-form-item>

            <!-- 小程序挂载 -->
            <el-form-item v-if="mountingType === 'mini'" label="小程序挂载" prop="mini_mounting_id">
              <el-select
                v-model="taskForm.mini_mounting_id"
                placeholder="请选择小程序挂载"
                size="large"
                style="width: 100%"
                filterable
                clearable
              >
                <el-option
                  v-for="item in miniMountingList"
                  :key="item.id"
                  :label="item.name"
                  :value="item.id"
                />
              </el-select>
            </el-form-item>
          </el-col>
        </el-row>

        <el-divider />

        <!-- 第二行：发布数量设置 -->
        <el-row :gutter="20">
          <el-col :span="12">
            <el-form-item label="总发布数" prop="total_publications">
              <el-input-number v-model="taskForm.total_publications" :min="1" size="large" style="width: 100%" />
            </el-form-item>

            <el-form-item label="每日发布数" prop="daily_maximum_release">
              <el-input-number v-model="taskForm.daily_maximum_release" :min="1" size="large" style="width: 100%" />
            </el-form-item>

            <el-form-item label="执行间隔(秒)" prop="fb_Interval">
              <el-input-number v-model="taskForm.fb_Interval" :min="10" :max="86400" size="large" style="width: 100%" />
            </el-form-item>
          </el-col>

          <el-col :span="12">
            <el-form-item label="发布时间设置">
              <el-radio-group v-model="timeSettingType" size="large" @change="handleTimeSettingChange">
                <el-radio-button label="range">时间范围</el-radio-button>
                <el-radio-button label="specific">具体时间</el-radio-button>
              </el-radio-group>
            </el-form-item>

            <el-form-item v-if="timeSettingType === 'range'" label="开始时间" prop="api_release_time_start">
              <el-time-select
                v-model="taskForm.api_release_time_start"
                :max-time="taskForm.api_release_time_end"
                placeholder="开始时间"
                start="00:00"
                step="01:00"
                end="24:00"
                size="large"
                style="width: 100%"
              />
            </el-form-item>

            <el-form-item v-if="timeSettingType === 'range'" label="结束时间" prop="api_release_time_end">
              <el-time-select
                v-model="taskForm.api_release_time_end"
                :min-time="taskForm.api_release_time_start"
                placeholder="结束时间"
                start="00:00"
                step="01:00"
                end="24:00"
                size="large"
                style="width: 100%"
              />
            </el-form-item>

            <el-form-item v-if="timeSettingType === 'specific'" label="具体时间">
              <el-select
                v-model="taskForm.every_hour"
                multiple
                filterable
                allow-create
                default-first-option
                placeholder="请输入时间并回车添加，如 09:30"
                size="large"
                style="width: 100%"
              >
                <el-option v-for="time in defaultTimes" :key="time" :label="time" :value="time" />
              </el-select>
            </el-form-item>

            <el-form-item label="发布算法" prop="article_publishing_algorithm">
              <el-radio-group v-model="taskForm.article_publishing_algorithm" size="large">
                <el-radio :label="1">仅发未发过的图片</el-radio>
                <el-radio :label="2">重复发送</el-radio>
              </el-radio-group>
            </el-form-item>
          </el-col>
        </el-row>

        <el-divider />

        <!-- 第三行：发布星期 -->
        <el-row :gutter="20">
          <el-col :span="24">
            <el-form-item label="发布星期" prop="week_day">
              <el-checkbox-group v-model="taskForm.week_day" size="large">
                <el-checkbox-button v-for="day in weekDays" :key="day.value" :label="day.value">
                  {{ day.label }}
                </el-checkbox-button>
              </el-checkbox-group>
            </el-form-item>
          </el-col>
        </el-row>

        <!-- 备注信息 -->
        <el-form-item label="备注信息">
          <el-input v-model="taskForm.notes" placeholder="请输入备注信息" type="textarea" :rows="3" />
        </el-form-item>
      </el-form>

      <template #footer>
        <button class="dialog-btn cancel-btn" @click="dialogVisible = false">取消</button>
        <button class="dialog-btn confirm-btn" @click="submitTaskForm">确认</button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import axios from 'axios';
import config from '@/config.js';
import Cookies from 'js-cookie';
import { Icon } from '@iconify/vue';
import { ElMessage, ElMessageBox } from 'element-plus';

export default {
  name: 'TwPublishDraft',
  components: { Icon },

  data() {
    return {
      // 平台类型列表
      platformTypes: [
        { label: '某音', value: 2 },
        { label: '某手', value: 3 },
        { label: '某微', value: 4 },
        { label: '小红薯', value: 5 },
        { label: 'B站', value: 6 },
        { label: '某TIK', value: 7 }
      ],

      // 加载状态
      loading: false,

      // 弹窗相关
      dialogVisible: false,
      dialogTitle: '新增任务',
      isEditMode: false,
      timeSettingType: 'range',
      mountingType: 'none',

      // 搜索表单
      searchForm: { task_name: '' },

      // 任务表单
      taskForm: {
        id: null,
        task_name: '',
        gzh_id: [],
        gzh_author: '',
        article_category_id: null,
        title_id: null,
        desc_id: null,
        topic_id: null,
        tp_num: 1,
        tp_id: null,
        shop_mounting_id: null,
        poi_mounting_id: null,
        mini_mounting_id: null,
        week_day: [1, 2, 3, 4, 5, 6, 7],
        every_hour: [],
        add_prefix_to_title: '',
        title_with_suffix: '',
        total_publications: 100,
        daily_maximum_release: 1,
        fb_Interval: 10,
        api_release_time_start: '00:00',
        api_release_time_end: '24:00',
        article_publishing_algorithm: 1,
        notes: ''
      },

      // 表单验证规则
      formRules: {
        task_name: [{ required: true, message: '请输入任务名称', trigger: 'blur' }],
        gzh_id: [{
          required: true,
          message: '请至少选择一个账号',
          trigger: 'change',
          type: 'array',
          validator: (rule, value, callback) => {
            if (this.isEditMode && (value === null || value === undefined || value === '')) {
              callback(new Error('请选择账号'));
            } else if (!this.isEditMode && (!Array.isArray(value) || value.length === 0)) {
              callback(new Error('请至少选择一个账号'));
            } else {
              callback();
            }
          }
        }],
        tp_num: [{ required: true, message: '请设置图片数量', trigger: 'blur' }],
        tp_id: [{ required: true, message: '请选择图库分组', trigger: 'change' }],
        total_publications: [{ required: true, message: '请输入总发布数', trigger: 'blur' }],
        daily_maximum_release: [{ required: true, message: '请输入每日发布数', trigger: 'blur' }],
        fb_Interval: [{ required: true, message: '请输入执行间隔', trigger: 'blur' }],
        api_release_time_start: [{ required: true, message: '请选择开始时间', trigger: 'change' }],
        api_release_time_end: [{ required: true, message: '请选择结束时间', trigger: 'change' }],
        article_publishing_algorithm: [{ required: true, message: '请选择发布算法', trigger: 'change' }],
        week_day: [{ required: true, message: '请选择发布星期', trigger: 'change' }]
      },

      // 列表数据
      taskList: [],
      gzhList: [],
      categoryList: [],
      titleList: [],
      descList: [],
      topicList: [],
      libraryList: [],
      shopMountingList: [],
      poiMountingList: [],
      miniMountingList: [],

      // 分页参数
      pagination: { page: 1, pageSize: 10, total: 0 },

      // 星期选项
      weekDays: [
        { label: '星期一', value: 1 },
        { label: '星期二', value: 2 },
        { label: '星期三', value: 3 },
        { label: '星期四', value: 4 },
        { label: '星期五', value: 5 },
        { label: '星期六', value: 6 },
        { label: '星期日', value: 7 }
      ],

      // 默认时间
      defaultTimes: ['08:00', '12:00', '18:00', '20:00', '22:00'],

      // 响应式宽度
      screenWidth: window.innerWidth
    }
  },

  computed: {
    userInfo() {
      return JSON.parse(Cookies.get('user_info') || '{}');
    },

    paginationLayout() {
      if (this.screenWidth < 576) return 'prev, pager, next';
      if (this.screenWidth < 768) return 'total, prev, pager, next';
      return 'total, sizes, prev, pager, next, jumper';
    },

    dialogWidth() {
      if (this.screenWidth < 576) return '95%';
      if (this.screenWidth < 768) return '90%';
      return '900px';
    },

    dialogTop() {
      if (this.screenWidth < 576) return '5vh';
      return '10vh';
    },

    formLabelWidth() {
      if (this.screenWidth < 576) return '80px';
      return '140px';
    }
  },

  mounted() {
    this.fetchTaskList();
    this.fetchGzhList();
    this.fetchCategoryList();
    this.fetchTitleList();
    this.fetchDescList();
    this.fetchTopicList();
    this.fetchLibraryList();
    this.fetchShopMountingList();
    this.fetchPoiMountingList();
    this.fetchMiniMountingList();
    window.addEventListener('resize', this.handleResize);
  },

  beforeUnmount() {
    window.removeEventListener('resize', this.handleResize);
  },

  methods: {
    // 响应式处理
    handleResize() {
      this.screenWidth = window.innerWidth;
    },

    // 全选账号
    selectAllAccounts() {
      this.taskForm.gzh_id = this.gzhList.map(gzh => gzh.id);
      ElMessage.success(`已全选 ${this.gzhList.length} 个账号`);
    },

    // 清空账号
    clearAllAccounts() {
      this.taskForm.gzh_id = [];
      ElMessage.info('已清空账号选择');
    },

    // 按平台选择账号
    selectPlatformAccounts(platformType) {
      const platformAccountIds = this.gzhList
        .filter(gzh => gzh.field_type === platformType)
        .map(gzh => gzh.id);
      const currentSelected = this.taskForm.gzh_id || [];
      const isAllSelected = platformAccountIds.length > 0 &&
        platformAccountIds.every(id => currentSelected.includes(id));

      if (isAllSelected) {
        this.taskForm.gzh_id = currentSelected.filter(id => !platformAccountIds.includes(id));
        ElMessage.info(`已取消选择 ${this.getPlatformText(platformType)} 平台账号`);
      } else {
        const otherPlatformAccounts = currentSelected.filter(id => {
          const gzh = this.gzhList.find(g => g.id === id);
          return gzh && gzh.field_type !== platformType;
        });
        this.taskForm.gzh_id = [...otherPlatformAccounts, ...platformAccountIds];
        ElMessage.success(`已选择 ${platformAccountIds.length} 个 ${this.getPlatformText(platformType)} 平台账号`);
      }
    },

    // 获取平台背景色
    getPlatformBgColor(type) {
      const colorMap = {
        2: '#FF2C3C',
        3: '#FF7800',
        4: '#07C160',
        5: '#FF2442',
        6: '#00A1D6',
        7: '#000000'
      };
      return colorMap[type] || '#909399';
    },

    // 获取账号平台类型
    getGzhFieldType(gzhId) {
      const gzh = this.gzhList.find(g => g.id == gzhId);
      return gzh ? gzh.field_type : null;
    },

    // 获取平台文本
    getPlatformText(type) {
      const platformMap = {
        2: '某音',
        3: '某手',
        4: '某微号',
        5: '小红薯',
        6: '小B站',
        7: '海外某音'
      };
      return platformMap[type] || `平台${type}`;
    },

    // 获取平台标签类型
    getPlatformTagType(type) {
      const typeMap = {
        2: 'danger',
        3: 'primary',
        4: 'success',
        5: 'warning',
        6: 'info',
        7: ''
      };
      return typeMap[type] || 'info';
    },

    // 获取账号名称
    getGzhName(id) {
      const gzh = this.gzhList.find(item => item.id === id);
      return gzh ? gzh.ghz_name : '未知';
    },

    // 获取图片分类名称
    getCategoryName(id) {
      const category = this.libraryList.find(item => item.id === id);
      return category ? category.tuku_name : '未知';
    },

    // 格式化日期时间
    formatDateTime(dateString) {
      if (!dateString) return null;
      const date = new Date(dateString);
      return `${date.getFullYear()}-${(date.getMonth() + 1).toString().padStart(2, '0')}-${date.getDate().toString().padStart(2, '0')} ${date.getHours().toString().padStart(2, '0')}:${date.getMinutes().toString().padStart(2, '0')}:${date.getSeconds().toString().padStart(2, '0')}`;
    },

    // 获取状态文本
    getStatusText(status) {
      const statusMap = {
        1: '待启动',
        2: '发布中',
        3: '发布完成',
        4: '发布失败'
      };
      return statusMap[status] || '未知状态';
    },

    // 获取状态标签类型
    getStatusTagType(status) {
      const typeMap = {
        1: 'info',
        2: 'warning',
        3: 'success',
        4: 'danger'
      };
      return typeMap[status] || '';
    },

    // 切换任务状态
    async toggleTaskStatus(row) {
      try {
        row.statusLoading = true;
        const newQdStatus = row.qd_status === 1 ? 2 : 1;
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/ks_tw_task_management.php`, {
          action: 'update_status',
          store_id: this.userInfo.id,
          id: row.id,
          qd_status: newQdStatus
        });
        if (data.success) {
          ElMessage.success('状态更新成功');
          row.qd_status = newQdStatus;
          row.status = newQdStatus === 1 ? 2 : 1;
        } else {
          ElMessage.error(data.error || '状态更新失败');
        }
      } catch (error) {
        ElMessage.error(`操作失败: ${error.response?.data?.error || error.message}`);
      } finally {
        row.statusLoading = false;
      }
    },

    // 获取任务列表
    async fetchTaskList() {
      this.loading = true;
      try {
        const params = {
          action: this.searchForm.task_name ? 'search' : 'list',
          store_id: this.userInfo.id,
          page: this.pagination.page,
          page_size: this.pagination.pageSize
        };
        if (this.searchForm.task_name) params.task_name = this.searchForm.task_name;
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/ks_tw_task_management.php`, params);
        if (data.data) {
          this.taskList = data.data.map(task => ({
            ...task,
            statusLoading: false,
            week_day: task.week_day ? JSON.parse(task.week_day) : [],
            every_hour: task.every_hour ? JSON.parse(task.every_hour) : []
          }));
          this.pagination.total = data.pagination?.total || data.data.length;
        }
      } catch (error) {
        ElMessage.error('获取任务列表失败');
      } finally {
        this.loading = false;
      }
    },

    // 获取账号列表
    async fetchGzhList() {
      try {
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/gzh_auth_management.php`, {
          action: 'list_all',
          store_id: this.userInfo.id
        });
        if (data.data) this.gzhList = data.data;
      } catch (error) {
        console.error('获取账号列表错误:', error);
      }
    },

    // 获取分类列表
    async fetchCategoryList() {
      try {
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/article_category_management.php`, {
          action: 'list_all',
          store_id: this.userInfo.id
        });
        if (data.data) this.categoryList = data.data;
      } catch (error) {
        console.error('获取分类列表错误:', error);
      }
    },

    // 获取标题分组列表
    async fetchTitleList() {
      try {
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/title_management.php`, {
          action: 'list_all',
          store_id: this.userInfo.id
        });
        if (data.data) this.titleList = data.data;
      } catch (error) {
        console.error('获取标题分组列表错误:', error);
      }
    },

    // 获取描述分组列表
    async fetchDescList() {
      try {
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/desc_management.php`, {
          action: 'list_all',
          store_id: this.userInfo.id
        });
        if (data.data) this.descList = data.data;
      } catch (error) {
        console.error('获取描述分组列表错误:', error);
      }
    },

    // 获取话题分组列表
    async fetchTopicList() {
      try {
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/topic_management.php`, {
          action: 'list_all',
          store_id: this.userInfo.id
        });
        if (data.data) this.topicList = data.data;
      } catch (error) {
        console.error('获取话题分组列表错误:', error);
      }
    },

    // 获取图库分组列表
    async fetchLibraryList() {
      try {
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/library_management.php`, {
          action: 'list_all',
          store_id: this.userInfo.id
        });
        if (data.data) this.libraryList = data.data;
      } catch (error) {
        console.error('获取图库分组列表错误:', error);
      }
    },

    // 获取商品挂载列表
    async fetchShopMountingList() {
      try {
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/shop_mounting_management.php`, {
          action: 'list_all',
          store_id: this.userInfo.id
        });
        if (data.data) this.shopMountingList = data.data;
      } catch (error) {
        console.error('获取商品挂载列表错误:', error);
      }
    },

    // 获取POI挂载列表
    async fetchPoiMountingList() {
      try {
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/poi_mounting_management.php`, {
          action: 'list_all',
          store_id: this.userInfo.id
        });
        if (data.data) this.poiMountingList = data.data;
      } catch (error) {
        console.error('获取POI挂载列表错误:', error);
      }
    },

    // 获取小程序挂载列表
    async fetchMiniMountingList() {
      try {
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/mini_mounting_management.php`, {
          action: 'list_all',
          store_id: this.userInfo.id
        });
        if (data.data) this.miniMountingList = data.data;
      } catch (error) {
        console.error('获取小程序挂载列表错误:', error);
      }
    },

    // 搜索处理
    handleSearch() {
      this.pagination.page = 1;
      this.fetchTaskList();
    },

    // 打开新增对话框
    openAddDialog() {
      this.dialogTitle = '新增任务';
      this.isEditMode = false;
      this.timeSettingType = 'range';
      this.mountingType = 'none';
      this.taskForm = {
        id: null,
        task_name: '',
        gzh_id: [],
        gzh_author: '',
        article_category_id: null,
        title_id: null,
        desc_id: null,
        topic_id: null,
        tp_num: 1,
        tp_id: null,
        shop_mounting_id: null,
        poi_mounting_id: null,
        mini_mounting_id: null,
        week_day: [1, 2, 3, 4, 5, 6, 7],
        every_hour: [],
        add_prefix_to_title: '',
        title_with_suffix: '',
        total_publications: 100,
        daily_maximum_release: 1,
        fb_Interval: 10,
        api_release_time_start: '00:00',
        api_release_time_end: '24:00',
        article_publishing_algorithm: 1,
        notes: ''
      };
      this.dialogVisible = true;
    },

    // 挂载类型变化处理
    handleMountingTypeChange(type) {
      if (type !== 'shop') this.taskForm.shop_mounting_id = null;
      if (type !== 'poi') this.taskForm.poi_mounting_id = null;
      if (type !== 'mini') this.taskForm.mini_mounting_id = null;
      if (type === 'none') {
        this.taskForm.shop_mounting_id = null;
        this.taskForm.poi_mounting_id = null;
        this.taskForm.mini_mounting_id = null;
      }
    },

    // 打开编辑对话框
    openEditDialog(row) {
      this.dialogTitle = '编辑任务';
      this.isEditMode = true;

      if (row.every_hour && row.every_hour.length > 0) {
        this.timeSettingType = 'specific';
      } else {
        this.timeSettingType = 'range';
      }

      if (row.shop_mounting_id) {
        this.mountingType = 'shop';
      } else if (row.poi_mounting_id) {
        this.mountingType = 'poi';
      } else if (row.mini_mounting_id) {
        this.mountingType = 'mini';
      } else {
        this.mountingType = 'none';
      }

      this.taskForm = {
        ...row,
        gzh_id: row.gzh_id ? [row.gzh_id] : [],
        api_release_time_start: this.formatTime(row.api_release_time_start),
        api_release_time_end: this.formatTime(row.api_release_time_end),
        week_day: row.week_day || [1, 2, 3, 4, 5, 6, 7],
        every_hour: row.every_hour || []
      };
      this.dialogVisible = true;
    },

    // 格式化时间
    formatTime(timeValue) {
      if (!timeValue) return '00:00';
      if (typeof timeValue === 'string' && /^\d+$/.test(timeValue)) {
        const num = parseInt(timeValue);
        if (num === 24) return '24:00';
        return `${num.toString().padStart(2, '0')}:00`;
      }
      if (typeof timeValue === 'number') {
        if (timeValue === 24) return '24:00';
        return `${timeValue.toString().padStart(2, '0')}:00`;
      }
      return timeValue;
    },

    // 发布时间设置变化处理
    handleTimeSettingChange(val) {
      if (val === 'range') {
        this.taskForm.every_hour = [];
      } else {
        this.taskForm.api_release_time_start = '00:00';
        this.taskForm.api_release_time_end = '24:00';
      }
    },

    // 提交任务表单
    submitTaskForm() {
      this.$refs.taskForm.validate(async (valid) => {
        if (valid) {
          try {
            const action = this.isEditMode ? 'update' : 'add';
            const payload = {
              ...this.taskForm,
              store_id: this.userInfo.id,
              week_day: JSON.stringify(this.taskForm.week_day)
            };

            if (this.isEditMode && Array.isArray(payload.gzh_id)) {
              payload.gzh_id = payload.gzh_id[0] || null;
            }

            if (this.timeSettingType === 'range') {
              payload.every_hour = '';
              payload.api_release_time_start = parseInt(payload.api_release_time_start.split(':')[0]);
              if (payload.api_release_time_end === '00:00') {
                payload.api_release_time_end = 24;
              } else {
                payload.api_release_time_end = parseInt(payload.api_release_time_end.split(':')[0]);
              }
            } else {
              payload.api_release_time_start = 0;
              payload.api_release_time_end = 0;
              payload.every_hour = JSON.stringify(payload.every_hour);
            }

            const { data } = await axios.post(`${config.BASE_URL}/shop/api/ks_tw_task_management.php`, {
              action,
              ...payload
            });

            if (data.success) {
              ElMessage.success(this.isEditMode ? '更新成功' : '添加成功');
              this.dialogVisible = false;
              this.fetchTaskList();
            } else {
              ElMessage.error(data.error || '操作失败');
            }
          } catch (error) {
            ElMessage.error(`操作失败: ${error.response?.data?.error || error.message}`);
          }
        }
      });
    },

    // 删除任务
    async handleDelete(id) {
      try {
        await ElMessageBox.confirm('确定要删除该任务吗？', '警告', {
          confirmButtonText: '确认删除',
          cancelButtonText: '取消',
          type: 'warning'
        });
        const { data } = await axios.post(`${config.BASE_URL}/shop/api/ks_tw_task_management.php`, {
          action: 'delete',
          id: id,
          store_id: this.userInfo.id
        });
        if (data.success) {
          ElMessage.success('删除成功');
          this.fetchTaskList();
        } else {
          ElMessage.error(data.error || '删除失败');
        }
      } catch (error) {
        if (error !== 'cancel') {
          ElMessage.error(`删除失败: ${error.response?.data?.error || error.message}`);
        }
      }
    }
  }
}
</script>

<style scoped lang="scss">
.draft-management-page {
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  overflow-x: hidden;
}

.settings-card {
  background: rgba(18, 14, 33, 0.6);
  backdrop-filter: blur(10px);
  border-radius: 20px;
  border: 1px solid rgba(157, 78, 221, 0.2);
  padding: 24px;
  width: 100%;
  box-sizing: border-box;
}

@media (max-width: 768px) {
  .settings-card {
    padding: 16px;
  }
}

@media (max-width: 576px) {
  .settings-card {
    padding: 12px;
  }
}

.page-title {
  font-size: 20px;
  font-weight: 600;
  color: #fff;
  margin-bottom: 24px;
  display: flex;
  align-items: center;
  gap: 10px;
}

.page-title svg {
  color: #c084fc;
}

@media (max-width: 768px) {
  .page-title {
    font-size: 18px;
    margin-bottom: 16px;
  }
}

.search-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 16px;
  margin-bottom: 20px;
}

.search-bar .search-left {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  flex: 1;
}

.search-bar .search-input {
  flex: 1;
  min-width: 200px;
}

@media (max-width: 768px) {
  .search-bar {
    flex-direction: column;
  }
  .search-bar .search-left {
    width: 100%;
  }
  .search-bar .search-left .el-input {
    width: 100% !important;
  }
  .search-bar .search-left .search-btn {
    width: 100%;
  }
  .search-bar .add-btn {
    width: 100%;
  }
}

.table-wrapper {
  position: relative;
  width: 100%;
  overflow-x: auto;
  margin-top: 20px;
  -webkit-overflow-scrolling: touch;
}

.table-wrapper::-webkit-scrollbar {
  height: 8px;
}

.table-wrapper::-webkit-scrollbar-track {
  background: rgba(157, 78, 221, 0.1);
  border-radius: 4px;
}

.table-wrapper::-webkit-scrollbar-thumb {
  background: rgba(157, 78, 221, 0.4);
  border-radius: 4px;
}

.table-wrapper::-webkit-scrollbar-thumb:hover {
  background: rgba(157, 78, 221, 0.6);
}

:deep(.el-table) {
  --el-table-bg-color: transparent !important;
  --el-table-tr-bg-color: transparent !important;
  --el-table-header-bg-color: transparent !important;
  --el-table-row-hover-bg-color: rgba(157, 78, 221, 0.1) !important;
  --el-table-current-row-bg-color: rgba(157, 78, 221, 0.15) !important;
  --el-table-stripe-row-bg-color: rgba(157, 78, 221, 0.05) !important;
  --el-table-border-color: rgba(157, 78, 221, 0.2) !important;
  background-color: transparent !important;
  color: #e0e0e0;
}

:deep(.el-table)::before {
  display: none;
}

:deep(.el-table .el-table__header-wrapper) {
  background-color: transparent !important;
}

:deep(.el-table .el-table__header-wrapper tr) {
  background-color: transparent !important;
}

:deep(.el-table .el-table__header-wrapper tr th:not(.el-table-fixed-column--right)) {
  background: rgba(157, 78, 221, 0.2) !important;
  color: #fff !important;
  font-weight: 600;
  border-bottom: 1px solid rgba(157, 78, 221, 0.3) !important;
}

:deep(.el-table .el-table__header-wrapper tr th:not(.el-table-fixed-column--right) .cell) {
  color: #fff !important;
}

:deep(.el-table .el-table__body-wrapper) {
  background-color: transparent !important;
}

:deep(.el-table .el-table__body-wrapper tr) {
  background-color: transparent !important;
}

:deep(.el-table .el-table__body-wrapper tr td:not(.el-table-fixed-column--right)) {
  background-color: transparent !important;
  border-bottom: 1px solid rgba(157, 78, 221, 0.1);
  color: #e0e0e0;
}

:deep(.el-table .el-table__body-wrapper tr.el-table__row--striped td:not(.el-table-fixed-column--right)) {
  background-color: rgba(157, 78, 221, 0.05) !important;
}

:deep(.el-table .el-table__body-wrapper tr:hover td:not(.el-table-fixed-column--right)) {
  background-color: rgba(157, 78, 221, 0.1) !important;
}

:deep(.el-table .el-table-fixed-column--right) {
  background-color: #120e21 !important;
  backdrop-filter: none !important;
}

:deep(.el-table th.el-table-fixed-column--right) {
  background-color: #2a1f45 !important;
}

:deep(.el-table .el-table__row--striped .el-table-fixed-column--right) {
  background-color: #1a1430 !important;
}

:deep(.el-table .el-table__empty-block) {
  background-color: transparent !important;
}

:deep(.el-table .el-table__empty-block .el-table__empty-text) {
  color: #a0a0c0 !important;
}

.action-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  border-radius: 8px;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  margin: 0 4px;
}

.action-btn.edit-btn {
  background: rgba(157, 78, 221, 0.15);
  border: 1px solid rgba(157, 78, 221, 0.4);
  color: #c084fc;
}

.action-btn.edit-btn:hover {
  background: rgba(157, 78, 221, 0.25);
}

.action-btn.delete-btn {
  background: rgba(239, 68, 68, 0.15);
  border: 1px solid rgba(239, 68, 68, 0.4);
  color: #f87171;
}

.action-btn.delete-btn:hover {
  background: rgba(239, 68, 68, 0.25);
}

@media (max-width: 576px) {
  .action-btn .btn-text {
    display: none;
  }
}

.pagination-container {
  margin-top: 24px;
  display: flex;
  justify-content: flex-end;
}

@media (max-width: 768px) {
  .pagination-container {
    justify-content: center;
  }
}

:deep(.el-pagination) {
  --el-pagination-bg-color: transparent;
  --el-pagination-button-bg-color: rgba(157, 78, 221, 0.1);
  --el-pagination-hover-color: #c084fc;
}

:deep(.el-pagination .btn-prev),
:deep(.el-pagination .btn-next),
:deep(.el-pagination .el-pager li) {
  background: rgba(157, 78, 221, 0.1);
  border: 1px solid rgba(157, 78, 221, 0.3);
  color: #a0a0c0;
  border-radius: 8px;
  min-width: 32px;
  height: 32px;
}

:deep(.el-pagination .btn-prev:hover),
:deep(.el-pagination .btn-next:hover),
:deep(.el-pagination .el-pager li:hover) {
  background: rgba(157, 78, 221, 0.2);
  color: #c084fc;
}

:deep(.el-pagination .el-pager li.active) {
  background: linear-gradient(135deg, #6546b4, #9d4edd);
  color: #fff;
  border-color: transparent;
}

:deep(.el-pagination .el-pagination__total),
:deep(.el-pagination .el-pagination__jump) {
  color: #a0a0c0;
}

:deep(.custom-dialog) {
  --el-dialog-bg-color: rgba(18, 14, 33, 0.98);
  --el-dialog-border-radius: 20px;
}

:deep(.custom-dialog .el-dialog__header) {
  border-bottom: 1px solid rgba(157, 78, 221, 0.2);
  padding: 20px 24px;
}

:deep(.custom-dialog .el-dialog__header .el-dialog__title) {
  color: #fff;
  font-weight: 600;
}

:deep(.custom-dialog .el-dialog__body) {
  padding: 24px;
}

:deep(.custom-dialog .el-dialog__footer) {
  border-top: 1px solid rgba(157, 78, 221, 0.2);
  padding: 16px 24px;
}

:deep(.el-form-item__label) {
  color: #a0a0c0;
  font-weight: 500;
}

:deep(.el-input__wrapper) {
  background: rgba(26, 21, 53, 0.8);
  border: 1px solid rgba(157, 78, 221, 0.3);
  border-radius: 10px;
  box-shadow: none;
}

:deep(.el-input__wrapper:hover) {
  border-color: rgba(157, 78, 221, 0.6);
}

:deep(.el-input__wrapper.is-focus) {
  border-color: #9d4edd;
  box-shadow: 0 0 0 2px rgba(157, 78, 221, 0.2);
}

:deep(.el-input__wrapper .el-input__inner) {
  color: #fff;
  background: transparent;
}

:deep(.el-input__wrapper .el-input__inner::placeholder) {
  color: #5a5a7a;
}

:deep(.el-textarea__inner) {
  background: rgba(26, 21, 53, 0.8);
  border: 1px solid rgba(157, 78, 221, 0.3);
  border-radius: 10px;
  color: #fff;
}

:deep(.el-textarea__inner:hover) {
  border-color: rgba(157, 78, 221, 0.6);
}

:deep(.el-textarea__inner:focus) {
  border-color: #9d4edd;
  box-shadow: 0 0 0 2px rgba(157, 78, 221, 0.2);
}

.dialog-btn {
  padding: 10px 20px;
  border-radius: 10px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  margin-left: 12px;
}

.dialog-btn.cancel-btn {
  background: rgba(157, 78, 221, 0.15);
  border: 1px solid rgba(157, 78, 221, 0.4);
  color: #a0a0c0;
}

.dialog-btn.cancel-btn:hover {
  background: rgba(157, 78, 221, 0.25);
  color: #c084fc;
}

.dialog-btn.confirm-btn {
  background: linear-gradient(135deg, #6546b4, #9d4edd);
  color: #fff;
}

.dialog-btn.confirm-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 4px 15px rgba(157, 78, 221, 0.4);
}

:deep(.el-button) {
  border-radius: 10px;
  padding: 10px 20px;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-weight: 500;
}

:deep(.el-button--primary) {
  background: linear-gradient(135deg, #6546b4, #9d4edd);
  border: none;
}

:deep(.el-button--primary):hover {
  transform: translateY(-1px);
}

:deep(.el-button--success) {
  background: linear-gradient(135deg, #10b981, #34d399);
  border: none;
}

:deep(.el-button--success):hover {
  transform: translateY(-1px);
}

.account-select-container {
  width: 100%;
}

.account-select {
  width: 100%;
  margin-bottom: 12px;
}

.selection-actions {
  background: rgba(18, 14, 33, 0.5);
  padding: 12px;
  border-radius: 8px;
  border: 1px solid rgba(157, 78, 221, 0.2);
  margin-top: 8px;
}

.quick-selection {
  display: flex;
  align-items: center;
  margin-bottom: 10px;
}

.platform-selection {
  display: flex;
  align-items: flex-start;
}

.action-label {
  font-size: 12px;
  color: #a0a0c0;
  margin-right: 10px;
  white-space: nowrap;
  line-height: 28px;
  flex-shrink: 0;
}

.selection-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 14px;
  border-radius: 8px;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;
  margin-right: 10px;
}

.selection-btn.select-all-btn {
  background: linear-gradient(135deg, #10b981, #34d399);
  color: #fff;
}

.selection-btn.select-all-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 2px 8px rgba(16, 185, 129, 0.3);
}

.selection-btn.clear-all-btn {
  background: rgba(239, 68, 68, 0.85);
  color: #fff;
}

.selection-btn.clear-all-btn:hover {
  background: rgb(239, 68, 68);
  transform: translateY(-1px);
}

.platform-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  align-items: center;
}

.platform-tag {
  padding: 2px 6px;
  border-radius: 4px;
  font-size: 11px;
  color: white;
  margin-left: 6px;
}

:deep(.el-divider) {
  border-color: rgba(157, 78, 221, 0.2);
  margin: 20px 0;
}

@media (max-width: 480px) {
  .btn-text {
    display: none;
  }
}
</style>

<!-- 非 scoped 全局样式 -->
<style lang="scss">
.el-dialog {
  --el-dialog-bg-color: rgba(18, 14, 33, 0.98) !important;
}

body>.el-overlay:has(.custom-dialog) .el-dialog__body {
  background: rgba(18, 14, 33, 0.98) !important;
}
</style>